import { useEffect, useState } from 'react';

const STORAGE_KEY = 'zr:whatsapp-inbox-message-overlay';
const CHANGE_EVENT = 'zr-whatsapp-inbox-message-overlay';
const MAX_IDS = 400;

interface EditedEntry {
  id: string;
  body: string;
}

interface OverlayBucket {
  hidden: string[];
  edited: EditedEntry[];
  revoked: string[];
}

type OverlayStore = Record<string, OverlayBucket>;

export interface WhatsAppInboxMessageOverlay {
  hidden: ReadonlySet<string>;
  edited: ReadonlyMap<string, string>;
  revoked: ReadonlySet<string>;
}

function emptyBucket(): OverlayBucket {
  return { hidden: [], edited: [], revoked: [] };
}

function readStore(): OverlayStore {
  if (typeof localStorage === 'undefined') {
    return {};
  }
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return {};
    }
    const store: OverlayStore = {};
    for (const [placeId, bucket] of Object.entries(parsed)) {
      if (!isRecord(bucket)) {
        continue;
      }
      store[placeId] = {
        hidden: stringIds(bucket['hidden']),
        edited: editedEntries(bucket['edited']),
        revoked: stringIds(bucket['revoked']),
      };
    }
    return store;
  } catch {
    return {};
  }
}

function writeStore(store: OverlayStore): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function remember(ids: readonly string[], messageId: string): string[] {
  const next = ids.filter((id) => id !== messageId);
  next.push(messageId);
  return next.slice(-MAX_IDS);
}

function rememberEdit(
  entries: readonly EditedEntry[],
  messageId: string,
  body: string,
): EditedEntry[] {
  const next = entries.filter((entry) => entry.id !== messageId);
  next.push({ id: messageId, body });
  return next.slice(-MAX_IDS);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function editedEntries(value: unknown): EditedEntry[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const entries: EditedEntry[] = [];
  for (const item of value) {
    if (typeof item === 'string') {
      entries.push({ id: item, body: '' });
      continue;
    }
    if (!isRecord(item)) {
      continue;
    }
    const { id, body } = item;
    if (typeof id === 'string' && typeof body === 'string') {
      entries.push({ id, body });
    }
  }
  return entries;
}

function stringIds(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === 'string');
}

function updateBucket(placeId: string, change: (bucket: OverlayBucket) => OverlayBucket): void {
  const store = readStore();
  const current = store[placeId] ?? emptyBucket();
  store[placeId] = change(current);
  writeStore(store);
}

export function hideWhatsAppInboxMessage(placeId: string, messageId: string): void {
  updateBucket(placeId, (bucket) => ({
    ...bucket,
    hidden: remember(bucket.hidden, messageId),
  }));
}

export function markWhatsAppInboxMessageEdited(
  placeId: string,
  messageId: string,
  body: string,
): void {
  updateBucket(placeId, (bucket) => ({
    ...bucket,
    edited: rememberEdit(bucket.edited, messageId, body),
  }));
}

export function markWhatsAppInboxMessageRevoked(placeId: string, messageId: string): void {
  updateBucket(placeId, (bucket) => ({
    ...bucket,
    revoked: remember(bucket.revoked, messageId),
  }));
}

export function readWhatsAppInboxMessageOverlay(placeId: string): WhatsAppInboxMessageOverlay {
  const bucket = readStore()[placeId] ?? emptyBucket();
  return {
    hidden: new Set(bucket.hidden),
    edited: new Map(
      bucket.edited.filter((entry) => entry.body.length > 0).map((entry) => [entry.id, entry.body]),
    ),
    revoked: new Set(bucket.revoked),
  };
}

export function useWhatsAppInboxMessageOverlay(placeId: string): WhatsAppInboxMessageOverlay {
  const [overlay, setOverlay] = useState(() => readWhatsAppInboxMessageOverlay(placeId));

  useEffect(() => {
    const refresh = () => {
      setOverlay(readWhatsAppInboxMessageOverlay(placeId));
    };
    refresh();
    window.addEventListener(CHANGE_EVENT, refresh);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
    };
  }, [placeId]);

  return overlay;
}
